import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { CheckCircle2, Eye, EyeOff, AlertCircle, User, Mail, Lock, Briefcase, Book, Clock, ShieldCheck, Loader2, GraduationCap } from 'lucide-react';
import { ACADEMIC_RANKS } from '../../utils/facultyOptions';

interface SignUpFormProps {
  onSuccess: () => void;
}

export function SignUpForm({ onSuccess }: SignUpFormProps) {
  const { signUp } = useAuth();
  const stepOrder: Array<'account' | 'role' | 'details' | 'password' | 'review'> = ['account', 'role', 'details', 'password', 'review'];
  const stepTitles: Record<(typeof stepOrder)[number], string> = {
    account: 'Basic Information',
    role: 'Role Selection',
    details: 'Academic Details',
    password: 'Security Setup',
    review: 'Review & Submit',
  };
  const stepDescriptions: Record<(typeof stepOrder)[number], string> = {
    account: 'Enter your personal and account identifiers.',
    role: 'Choose the role that matches your access permissions.',
    details: 'Provide your academic profile information.',
    password: 'Create secure credentials for your account.',
    review: 'Check all details before final submission.',
  };
  const [step, setStep] = useState<'account' | 'role' | 'details' | 'password' | 'review'>('account');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    idNumber: '',
    role: '' as 'student' | 'faculty' | '',
    yearLevel: '',
    course: '',
    position: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldsFocused, setFieldsFocused] = useState<Record<string, boolean>>({});
  const [validation, setValidation] = useState<Record<string, boolean>>({});
  const [courseOptions, setCourseOptions] = useState<{ id: string; code: string; name: string }[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  // Fetch available courses/programs for the dropdown
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoadingCourses(true);
        const res = await api.getPublicCourses();
        setCourseOptions((res.courses || []).map((c: any) => ({ id: c.id, code: c.code, name: c.name })));
      } catch (err) {
        console.error('Failed to load courses:', err);
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  // Validate each field
  useEffect(() => {
    setValidation({
      name: formData.name.length >= 2,
      email: formData.email.includes('@') && formData.email.includes('.'),
      password: formData.password.length >= 8 && /[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) && /[0-9]/.test(formData.password),
      confirmPassword: formData.password === formData.confirmPassword && formData.password.length >= 8,
      idNumber: formData.idNumber.length > 0,
      course: formData.role !== '' && formData.course.length > 0,
      yearLevel: formData.role !== 'student' || formData.yearLevel.length > 0,
      position: formData.role !== 'faculty' || formData.position.length > 0,
    });
  }, [formData]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const stepIcons = {
    account: <User className="w-5 h-5" />,
    role: <Briefcase className="w-5 h-5" />,
    details: <Book className="w-5 h-5" />,
    password: <Lock className="w-5 h-5" />,
    review: <CheckCircle2 className="w-5 h-5" />,
  };

  const getStepCompleted = (stepName: string) => {
    switch(stepName) {
      case 'account': return validation.name && validation.email && validation.idNumber;
      case 'role': return formData.role.length > 0;
      case 'details': return validation.course && validation.yearLevel && validation.position;
      case 'password': return validation.password && validation.confirmPassword;
      case 'review': return true;
      default: return false;
    }
  };

  const canProceed = () => {
    switch(step) {
      case 'account': return validation.name && validation.email && validation.idNumber;
      case 'role': return formData.role.length > 0;
      case 'details': return validation.course && validation.yearLevel && validation.position;
      case 'password': return validation.password && validation.confirmPassword;
      case 'review': return true;
      default: return false;
    }
  };

  const nextStep = () => {
    if (!canProceed()) return;
    const currentIndex = stepOrder.indexOf(step);
    if (currentIndex < stepOrder.length - 1) {
      setStep(stepOrder[currentIndex + 1]);
    }
  };

  const prevStep = () => {
    const currentIndex = stepOrder.indexOf(step);
    if (currentIndex > 0) {
      setStep(stepOrder[currentIndex - 1]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const signUpData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        idNumber: formData.idNumber,
        role: formData.role as 'student' | 'faculty',
        yearLevel: formData.yearLevel ? parseInt(formData.yearLevel) : undefined,
        course: formData.course || undefined,
        position: formData.position || undefined,
      };

      const result = await signUp(signUpData);
      setSuccessMessage(result.message);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="animate-in fade-in space-y-4 py-6 text-center sm:py-8">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg sm:h-20 sm:w-20">
            <Clock className="h-8 w-8 text-white sm:h-10 sm:w-10" />
          </div>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Registration Submitted!</h3>
          <p className="mt-2 px-2 text-sm text-gray-600 sm:px-4">
            {successMessage || 'Your account is pending administrator verification.'}
          </p>
        </div>
        <div className="mx-1 rounded-lg border border-blue-200 bg-blue-50 p-3 sm:mx-4 sm:p-4">
          <div className="flex items-center gap-2 justify-center mb-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800 text-sm">What happens next?</span>
          </div>
          <ol className="text-xs text-blue-700 text-left space-y-1 pl-4 list-decimal">
            <li>An administrator will review your registration</li>
            <li>Once approved, you will receive an email notification</li>
            <li>You can then log in with your credentials</li>
          </ol>
        </div>
        <Button
          onClick={onSuccess}
          variant="outline"
          className="mt-4"
        >
          Back to Sign In
        </Button>
      </div>
    );
  }

  const currentStepIndex = stepOrder.indexOf(step);
  const progressPercent = ((currentStepIndex + 1) / stepOrder.length) * 100;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
      <div className="space-y-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3 shadow-sm sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Registration Flow</p>
            <p className="text-sm font-semibold tracking-tight text-slate-900 sm:text-base">{stepTitles[step]}</p>
          </div>
          <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 sm:text-xs">
            Step {currentStepIndex + 1} of {stepOrder.length}
          </span>
        </div>
        <p className="text-xs leading-5 text-slate-500 sm:text-sm">{stepDescriptions[step]}</p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-[#1877F2] transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-5 gap-1.5 rounded-2xl border border-slate-200 bg-slate-100/90 p-1.5 sm:gap-2 sm:p-2">
        {stepOrder.map((stepName) => (
          <div key={stepName} className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => {
                const stepIndex = stepOrder.indexOf(step);
                const targetIndex = stepOrder.indexOf(stepName);
                if (targetIndex < stepIndex) {
                  setStep(stepName);
                }
              }}
              disabled={stepOrder.indexOf(stepName) > stepOrder.indexOf(step)}
              className={`mb-1.5 flex h-9 w-full items-center justify-center rounded-xl px-1 text-xs font-semibold transition-all duration-200 sm:mb-2 sm:h-10 sm:text-sm ${
                step === stepName
                  ? 'bg-white text-[#1877F2] ring-1 ring-[#1877F2]/20 shadow-sm'
                  : getStepCompleted(stepName)
                  ? 'bg-emerald-500 text-white'
                  : 'bg-transparent text-slate-500'
              }`}
              aria-label={`Go to ${stepTitles[stepName]}`}
            >
              <span className="hidden sm:inline">{stepName.charAt(0).toUpperCase() + stepName.slice(1)}</span>
              <span className="sm:hidden">{getStepCompleted(stepName) ? <CheckCircle2 className="h-4 w-4" /> : stepIcons[stepName as keyof typeof stepIcons]}</span>
            </button>
          </div>
        ))}
      </div>

      <div className="animate-in fade-in space-y-3 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm duration-300 sm:space-y-4 sm:p-5 md:p-6">
        {step === 'account' && (
          <>
            <div className="flex items-center gap-2 text-slate-900">
              <GraduationCap className="h-4 w-4 text-[#1877F2]" />
              <h3 className="text-base font-semibold sm:text-lg">Basic account details</h3>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="sr-only">Full Name</Label>
                <div className="relative">
                  <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${
                    fieldsFocused.name ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Full name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    onFocus={() => setFieldsFocused(prev => ({ ...prev, name: true }))}
                    onBlur={() => setFieldsFocused(prev => ({ ...prev, name: false }))}
                    className="h-11 rounded-xl border-slate-200 pl-8 text-[15px] md:pl-10 sm:h-12"
                    required
                  />
                  {validation.name && formData.name && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="idNumber" className="sr-only">ID Number</Label>
                <Input
                  id="idNumber"
                  type="text"
                  placeholder="ID No."
                  value={formData.idNumber}
                  onChange={(e) => handleChange('idNumber', e.target.value)}
                  className="h-11 rounded-xl border-slate-200 text-[15px] sm:h-12"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="sr-only">Email Address</Label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${
                  fieldsFocused.email ? 'text-blue-600' : 'text-gray-400'
                }`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="Email address (e.g., yourname@school.edu)"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onFocus={() => setFieldsFocused(prev => ({ ...prev, email: true }))}
                  onBlur={() => setFieldsFocused(prev => ({ ...prev, email: false }))}
                  className="h-11 rounded-xl border-slate-200 pl-8 text-[15px] md:pl-10 sm:h-12"
                  required
                />
                {validation.email && formData.email && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
              </div>
            </div>
          </>
        )}

        {step === 'role' && (
          <div className="space-y-4">
            <div className="space-y-1 text-center">
              <h3 className="text-base font-semibold text-slate-900 sm:text-lg">Choose your role</h3>
              <p className="text-xs text-slate-500 sm:text-sm">Select one role to continue with the right registration fields.</p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {(['student', 'faculty'] as const).map((roleOption) => (
                <button
                  key={roleOption}
                  type="button"
                  onClick={() => handleChange('role', roleOption)}
                  aria-pressed={formData.role === roleOption}
                  className={`rounded-2xl border p-3.5 text-left transition-all duration-200 sm:p-4 ${
                    formData.role === roleOption
                      ? 'border-[#1877F2] bg-blue-50/80 ring-1 ring-[#1877F2]/20 shadow-[0_14px_24px_rgba(24,119,242,0.12)]'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold capitalize tracking-tight text-slate-900 sm:text-base">{roleOption}</span>
                        {formData.role === roleOption && <CheckCircle2 className="h-4 w-4 text-[#1877F2]" />}
                      </div>
                      <p className="mt-2 text-xs leading-5 text-slate-600 sm:text-sm sm:leading-6">
                        {roleOption === 'student' && 'Register your program and year level to access subject offerings.'}
                        {roleOption === 'faculty' && 'Register your program assignment and academic rank for verification.'}
                      </p>
                    </div>
                    <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${formData.role === roleOption ? 'bg-[#1877F2] text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {roleOption === 'student' ? <Book className="h-5 w-5" /> : <Briefcase className="h-5 w-5" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'details' && (
          <>
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-slate-900 sm:text-lg">Academic details</h3>
              <p className="text-xs leading-5 text-slate-500 sm:text-sm">
                {formData.role === 'student'
                  ? 'Select your current program and year level.'
                  : 'Select the program you are affiliated with and your academic rank.'}
              </p>
            </div>

            {formData.role === 'student' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="course" className="sr-only">Program</Label>
                  {loadingCourses ? (
                    <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading programs…
                    </div>
                  ) : courseOptions.length > 0 ? (
                    <Select value={formData.course} onValueChange={(value) => handleChange('course', value)}>
                      <SelectTrigger className="h-11 rounded-xl border-slate-200 sm:h-12">
                        <SelectValue placeholder="Select your program" />
                      </SelectTrigger>
                      <SelectContent>
                        {courseOptions.map(course => (
                          <SelectItem key={course.id} value={course.code}>
                            {course.code} — {course.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="course"
                      type="text"
                      placeholder="e.g., MAED, MSCS, EdD-EM"
                      value={formData.course}
                      onChange={(e) => handleChange('course', e.target.value)}
                      className="h-11 rounded-xl border-slate-200 text-[15px] sm:h-12"
                    />
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="yearLevel" className="sr-only">Year Level</Label>
                  <Select value={formData.yearLevel} onValueChange={(value) => handleChange('yearLevel', value)}>
                    <SelectTrigger className="h-11 rounded-xl border-slate-200 sm:h-12">
                      <SelectValue placeholder="Select your year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1st Year</SelectItem>
                      <SelectItem value="2">2nd Year</SelectItem>
                      <SelectItem value="3">3rd Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {formData.role === 'faculty' && (
              <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="facultyProgram" className="sr-only">Program</Label>
                  {loadingCourses ? (
                    <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading programs…
                    </div>
                  ) : courseOptions.length > 0 ? (
                    <Select value={formData.course} onValueChange={(value) => handleChange('course', value)}>
                      <SelectTrigger id="facultyProgram" className="h-11 rounded-xl border-slate-200 sm:h-12">
                        <SelectValue placeholder="Select faculty program" />
                      </SelectTrigger>
                      <SelectContent>
                        {courseOptions.map(course => (
                          <SelectItem key={course.id} value={course.code}>
                            {course.code} — {course.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="facultyProgram"
                      type="text"
                      placeholder="Enter faculty program"
                      value={formData.course}
                      onChange={(e) => handleChange('course', e.target.value)}
                      className="h-11 rounded-xl border-slate-200 text-[15px] sm:h-12"
                    />
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="position" className="sr-only">Position</Label>
                  <Select value={formData.position} onValueChange={(value) => handleChange('position', value)}>
                    <SelectTrigger id="position" className="h-11 rounded-xl border-slate-200 sm:h-12">
                      <SelectValue placeholder="Select academic rank" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACADEMIC_RANKS.map((rank) => (
                        <SelectItem key={rank} value={rank}>
                          {rank}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </>
        )}

        {step === 'password' && (
          <>
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-slate-900 sm:text-lg">Secure your account</h3>
              <p className="text-xs leading-5 text-slate-500 sm:text-sm">Use a strong password with uppercase, lowercase, and numbers.</p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="sr-only">Password</Label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${
                    fieldsFocused.password ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    onFocus={() => setFieldsFocused(prev => ({ ...prev, password: true }))}
                    onBlur={() => setFieldsFocused(prev => ({ ...prev, password: false }))}
                    className="h-11 rounded-xl border-slate-200 pl-8 pr-8 text-[15px] md:pl-10 md:pr-10 sm:h-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword" className="sr-only">Confirm Password</Label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${
                    fieldsFocused.confirmPassword ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    onFocus={() => setFieldsFocused(prev => ({ ...prev, confirmPassword: true }))}
                    onBlur={() => setFieldsFocused(prev => ({ ...prev, confirmPassword: false }))}
                    className="h-11 rounded-xl border-slate-200 pl-8 pr-8 text-[15px] md:pl-10 md:pr-10 sm:h-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Passwords do not match
                  </p>
                )}
                {validation.confirmPassword && formData.confirmPassword && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Passwords match
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4">
              <div className="mt-0 flex items-center gap-2">
                <div className="h-2 flex-1 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${
                    formData.password.length === 0 ? 'w-0' :
                    formData.password.length < 8 ? 'w-1/4 bg-red-500' :
                    !(/[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) && /[0-9]/.test(formData.password)) ? 'w-1/2 bg-yellow-500' :
                    formData.password.length < 12 ? 'w-3/4 bg-blue-500' :
                    'w-full bg-green-500'
                  }`} />
                </div>
                <span className="text-xs text-gray-600">
                  {formData.password.length === 0 ? 'Min. 8' : 
                   formData.password.length < 8 ? 'Too short' :
                   !(/[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) && /[0-9]/.test(formData.password)) ? 'Needs A-Z, a-z, 0-9' :
                   formData.password.length < 12 ? 'Good' : 'Strong'}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-gray-500 sm:text-xs">
                Include uppercase, lowercase, and a number
              </div>
            </div>
          </>
        )}

        {step === 'review' && (
          <div className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-slate-900 sm:text-lg">Review your information</h3>
              <p className="text-xs leading-5 text-slate-500 sm:text-sm">Confirm everything before submitting for administrator approval.</p>
            </div>
            
            <div className="rounded-2xl border border-blue-100 bg-blue-50/70">
              <div className="flex justify-between gap-2 border-b border-blue-100 px-3 py-2.5 sm:px-4">
                <span className="text-sm text-slate-600">Name</span>
                <span className="text-right text-sm font-semibold text-slate-900">{formData.name}</span>
              </div>
              <div className="flex justify-between gap-2 border-b border-blue-100 px-3 py-2.5 sm:px-4">
                <span className="text-sm text-slate-600">Email</span>
                <span className="text-right text-sm font-semibold text-slate-900 break-all">{formData.email}</span>
              </div>
              <div className="flex justify-between gap-2 border-b border-blue-100 px-3 py-2.5 sm:px-4">
                <span className="text-sm text-slate-600">ID Number</span>
                <span className="text-right text-sm font-semibold text-slate-900">{formData.idNumber}</span>
              </div>
              <div className="flex justify-between gap-2 border-b border-blue-100 px-3 py-2.5 sm:px-4">
                <span className="text-sm text-slate-600">Role</span>
                <span className="text-right text-sm font-semibold capitalize text-slate-900">{formData.role}</span>
              </div>
              {formData.role === 'student' && (
                <>
                  <div className="flex justify-between gap-2 border-b border-blue-100 px-3 py-2.5 sm:px-4">
                    <span className="text-sm text-slate-600">Program</span>
                    <span className="text-right text-sm font-semibold text-slate-900">{formData.course || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-2 px-3 py-2.5 sm:px-4">
                    <span className="text-sm text-slate-600">Year Level</span>
                    <span className="text-right text-sm font-semibold text-slate-900">{formData.yearLevel ? `Year ${formData.yearLevel}` : '—'}</span>
                  </div>
                </>
              )}
              {formData.role === 'faculty' && (
                <>
                  <div className="flex justify-between gap-2 border-b border-blue-100 px-3 py-2.5 sm:px-4">
                    <span className="text-sm text-slate-600">Program</span>
                    <span className="text-right text-sm font-semibold text-slate-900">{formData.course || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-2 px-3 py-2.5 sm:px-4">
                    <span className="text-sm text-slate-600">Position</span>
                    <span className="text-right text-sm font-semibold text-slate-900">{formData.position || '—'}</span>
                  </div>
                </>
              )}
            </div>

            <p className="text-center text-xs text-slate-500 sm:text-sm">
              By creating an account, you agree to use this system for academic purposes only.
            </p>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className="border-red-200 bg-red-50 animate-in fade-in text-xs sm:text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2.5 pt-1 sm:gap-3">
        {step !== 'account' && (
          <Button
            type="button"
            onClick={prevStep}
            variant="outline"
            className="h-11 flex-1 rounded-xl border-slate-300 hover:bg-slate-50 sm:h-12"
          >
            Back
          </Button>
        )}
        
        {step !== 'review' && (
          <Button
            type="button"
            onClick={nextStep}
            disabled={!canProceed()}
            className="h-11 flex-1 rounded-xl bg-[#1877F2] font-semibold hover:bg-[#166fe5] disabled:opacity-50 sm:h-12"
          >
            Next
          </Button>
        )}

        {step === 'review' && (
          <Button
            type="submit"
            disabled={isLoading}
            className="h-11 flex-1 rounded-xl bg-[#1877F2] font-semibold hover:bg-[#166fe5] sm:h-12"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        )}
      </div>
    </form>
  );
}
